'use client'

/**
 * React readers for the one running-recording session (#13803).
 *
 * The store itself is framework-free and lives in
 * `@/lib/notetaker/recording-session-store`; this is the only place that
 * subscribes to it from React, so every reader is guaranteed to be looking at
 * the same clock rather than at a counter of its own.
 *
 * Two hooks rather than one, because the two readers want different slices.
 * The floating bar renders the level meter and re-renders at the rate the
 * meter is published; the meeting page renders only the elapsed time and must
 * NOT re-render at that rate, so it subscribes to the number alone.
 */

import { useMemo, useSyncExternalStore } from 'react'

import {
  getRecordingSessionSnapshot,
  getServerRecordingSessionSnapshot,
  type RecordingSessionSnapshot,
  subscribeToRecordingSession,
} from '@/lib/notetaker/recording-session-store'

/** The whole session — status, ids, elapsed time and input level. */
export function useRecordingSession(): RecordingSessionSnapshot {
  return useSyncExternalStore(
    subscribeToRecordingSession,
    getRecordingSessionSnapshot,
    getServerRecordingSessionSnapshot,
  )
}

/** What a running capture records into (#14931); `null` while idle. */
export type LiveRecordingTarget = {
  meetingId: string | null
  noteId: string | null
}

function liveTargetKey(): string {
  const session = getRecordingSessionSnapshot()
  return session.status === 'idle'
    ? ''
    : `${session.meetingId ?? ''}\u0000${session.noteId ?? ''}`
}

/**
 * The meeting and note a running capture records into, or `null` — and
 * nothing else, so a Coming up row re-renders when a recording starts or
 * stops, never at the rate the level meter is published. The snapshot is a
 * string, which `useSyncExternalStore` compares by value.
 */
export function useLiveRecordingTarget(): LiveRecordingTarget | null {
  const key = useSyncExternalStore(
    subscribeToRecordingSession,
    liveTargetKey,
    () => '',
  )
  return useMemo(() => {
    if (key === '') return null
    const [meetingId = '', noteId = ''] = key.split('\u0000')
    return { meetingId: meetingId || null, noteId: noteId || null }
  }, [key])
}
